import * as RadixToast from '@radix-ui/react-toast';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './toast.component.module.css';
import { dismissToast, subscribeToasts, type ToastRecord } from './toast-store';

// Raise toasts with `toast(...)` / `toastApiError(...)` from './toast-store'.

/**
 * Renders the toast stack. Mount once near the app root; raise toasts from
 * anywhere with `toast(...)` (or wire `toastApiError` into React Query's onError).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	const [toasts, setToasts] = useState<ToastRecord[]>([]);

	useEffect(() => subscribeToasts(setToasts), []);

	return (
		<RadixToast.Provider swipeDirection="right">
			{children}
			{toasts.map((entry) => (
				<RadixToast.Root
					key={entry.id}
					className={styles.toast}
					data-variant={entry.variant ?? 'info'}
					duration={entry.duration ?? 5000}
					onOpenChange={(open) => {
						if (!open) {
							dismissToast(entry.id);
						}
					}}
				>
					<div className={styles.body}>
						<RadixToast.Title className={styles.title}>{entry.title}</RadixToast.Title>
						{entry.description !== undefined && (
							<RadixToast.Description className={styles.description}>
								{entry.description}
							</RadixToast.Description>
						)}
					</div>
					{entry.action ? (
						<RadixToast.Action asChild altText={entry.action.label}>
							<button
								type="button"
								className={styles.action}
								onClick={() => {
									entry.action?.onClick();
									dismissToast(entry.id);
								}}
							>
								{entry.action.label}
							</button>
						</RadixToast.Action>
					) : null}
					<RadixToast.Close className={styles.close} aria-label={t('a11y.dismiss', 'Dismiss')}>
						<svg viewBox="0 0 16 16" fill="none" aria-hidden focusable="false">
							<path
								d="m4 4 8 8m0-8-8 8"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>
					</RadixToast.Close>
				</RadixToast.Root>
			))}
			<RadixToast.Viewport className={styles.viewport} />
		</RadixToast.Provider>
	);
}
